/**
 * MMM landing page — mirrors the Experiments landing page exactly
 * (pages/experiments/index.tsx): a list of past runs + "AI Mode" /
 * "New MMM" buttons. The "active model + ROI + scenario + chat" view
 * that used to live here moved to the view route, /mmm/:id (MMMDashboard,
 * unchanged), reached by clicking a completed run's card.
 *
 * Fetches job configs + job runs in parallel, joins them by jobConfigId so
 * the status badge reflects the live job run state — same approach as
 * Experiments, so MMM and Experiments stay in sync if that join logic
 * changes again later.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import axiosInstance from "../../utils/axiosInstance";

// ── Types ──────────────────────────────────────────────────────────────────────

type RunStatus = "pending" | "running" | "completed" | "failed";

interface MMMRun {
  id: string;
  name: string;
  status: RunStatus;
  createdAt: string;
  channels?: string[];
  modelType?: string;
  isSeasonal?: boolean;
  jobRunId?: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function mapStatus(configStatus: string, runStatus?: string): RunStatus {
  const run = (runStatus || "").toUpperCase();
  const cfg = (configStatus || "").toLowerCase();

  if (run === "RUNNING")                                 return "running";
  if (run === "PENDING")                                 return "pending";
  if (run === "SUCCESSFUL" || run === "SUCCESS")         return "completed";
  if (run === "FAILED")                                  return "failed";

  if (cfg.includes("success") || cfg.includes("complete")) return "completed";
  if (cfg === "running")                                 return "running";
  if (cfg.includes("fail"))                               return "failed";
  return "pending";
}

function fmt(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const channelLabel = (raw: string) =>
  raw.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

// ── Status badge ───────────────────────────────────────────────────────────────

const STATUS_META: Record<RunStatus, { label: string; color: string; bg: string }> = {
  pending:   { label: "Queued",     color: "#3b82f6", bg: "rgba(59,130,246,0.1)" },
  running:   { label: "Running",    color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
  completed: { label: "Completed",  color: "#10b981", bg: "rgba(16,185,129,0.1)" },
  failed:    { label: "Failed",     color: "#ef4444", bg: "rgba(239,68,68,0.1)"  },
};

const StatusBadge: React.FC<{ status: RunStatus }> = ({ status }) => {
  const m = STATUS_META[status];
  const pulse = status === "running" || status === "pending";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 9px", borderRadius: 20,
      background: m.bg, color: m.color,
      fontSize: 11, fontWeight: 500, fontFamily: "var(--font-sans)",
      whiteSpace: "nowrap",
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: "50%", background: m.color,
        animation: pulse ? "pulseDot 1.5s ease-in-out infinite" : "none",
      }} />
      {m.label}
    </span>
  );
};

// ── Run card ───────────────────────────────────────────────────────────────────

const RunCard: React.FC<{
  run: MMMRun;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}> = ({ run, onClick, onDelete }) => {
  const m = STATUS_META[run.status];
  const isClickable = run.status === "completed";
  return (
    <div
      onClick={isClickable ? onClick : undefined}
      title={isClickable ? undefined : "This model isn't ready to view yet"}
      style={{
        padding: "16px 20px",
        background: "var(--bg-surface)",
        border: "1px solid var(--border-subtle)",
        borderLeft: `3px solid ${m.color}`,
        borderRadius: "var(--radius)",
        cursor: isClickable ? "pointer" : "default",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
        position: "relative",
      }}
      onMouseEnter={e => { if (isClickable) e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}
    >
      {/* Delete button */}
      <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 2 }}>
        <button
          onClick={onDelete}
          title="Delete run"
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-tertiary)", fontSize: 14, padding: 4,
            borderRadius: 4, lineHeight: 1, transition: "color 150ms ease",
          }}
          onMouseEnter={e => (e.currentTarget.style.color = "#ef4444")}
          onMouseLeave={e => (e.currentTarget.style.color = "var(--text-tertiary)")}
        >
          ✕
        </button>
      </div>

      {/* Title row */}
      <div style={{ marginBottom: 10, paddingRight: 44 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", fontFamily: "var(--font-sans)" }}>
            {run.name}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <StatusBadge status={run.status} />
          <span style={{ fontSize: 11, color: "var(--text-tertiary)", fontFamily: "var(--font-sans)" }}>
            {fmt(run.createdAt)}
          </span>
        </div>
      </div>

      {/* Meta chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: run.status === "completed" ? 12 : 0 }}>
        {run.channels && run.channels.length > 0 && (
          <span style={chipStyle}>{run.channels.length} channel{run.channels.length === 1 ? "" : "s"}</span>
        )}
        {run.modelType && (
          <span style={chipStyle}>{run.modelType === "sales-model" ? "Sales model" : run.modelType}</span>
        )}
        {run.isSeasonal && <span style={chipStyle}>Seasonal</span>}
      </div>

      {/* Channels list — only for completed */}
      {run.status === "completed" && run.channels && run.channels.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, paddingTop: 10, borderTop: "1px solid var(--border-subtle)" }}>
          {run.channels.slice(0, 4).map(c => (
            <span key={c} style={{ fontSize: 11.5, color: "var(--text-secondary)", fontFamily: "var(--font-sans)" }}>
              {channelLabel(c)}{run.channels && run.channels.indexOf(c) < Math.min(3, run.channels.length - 1) ? "," : ""}
            </span>
          ))}
          {run.channels.length > 4 && (
            <span style={{ fontSize: 11.5, color: "var(--text-tertiary)", fontFamily: "var(--font-sans)" }}>
              +{run.channels.length - 4} more
            </span>
          )}
        </div>
      )}

      {/* Progress bar — running / pending */}
      {(run.status === "running" || run.status === "pending") && (
        <div style={{ paddingTop: 10, borderTop: "1px solid var(--border-subtle)" }}>
          <div style={{ fontSize: 11, color: "var(--text-tertiary)", fontFamily: "var(--font-sans)", marginBottom: 5 }}>
            {run.status === "pending" ? "Queued — waiting to start" : "Training model…"}
          </div>
          <div style={{ height: 3, background: "var(--bg-hover)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 2,
              width: run.status === "running" ? "60%" : "15%",
              background: m.color,
              animation: "indeterminate 2s ease-in-out infinite",
            }} />
          </div>
        </div>
      )}
    </div>
  );
};

const chipStyle: React.CSSProperties = {
  padding: "2px 8px", borderRadius: 12,
  background: "var(--bg-hover)", color: "var(--text-secondary)",
  fontSize: 11, fontFamily: "var(--font-sans)",
};

// ── Filter tabs ────────────────────────────────────────────────────────────────

type FilterKey = "completed" | "active" | "failed";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "completed", label: "Completed" },
  { key: "active",    label: "Active"    },
  { key: "failed",    label: "Failed"    },
];

function matchFilter(r: MMMRun, f: FilterKey): boolean {
  if (f === "active")    return r.status === "running" || r.status === "pending";
  if (f === "completed") return r.status === "completed";
  if (f === "failed")    return r.status === "failed";
  return true;
}

// ── Delete confirm modal ───────────────────────────────────────────────────────

const DeleteModal: React.FC<{
  run: MMMRun;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ run, onConfirm, onCancel, loading }) => (
  <div
    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
    onClick={onCancel}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{ background: "var(--bg-surface)", borderRadius: "var(--radius)", padding: "24px 28px", maxWidth: 380, width: "90%", border: "1px solid var(--border-default)" }}
    >
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 8, fontFamily: "var(--font-sans)" }}>
        Delete "{run.name}"?
      </div>
      <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 20, fontFamily: "var(--font-sans)", lineHeight: 1.5 }}>
        This will permanently delete the model run, all associated job runs, and S3 data. This cannot be undone.
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} disabled={loading} style={cancelBtnStyle}>Cancel</button>
        <button onClick={onConfirm} disabled={loading} style={deleteBtnStyle}>
          {loading ? "Deleting…" : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

const cancelBtnStyle: React.CSSProperties = {
  padding: "7px 16px", borderRadius: "var(--radius)",
  background: "var(--bg-hover)", color: "var(--text-secondary)",
  border: "1px solid var(--border-default)", fontSize: 13,
  cursor: "pointer", fontFamily: "var(--font-sans)",
};
const deleteBtnStyle: React.CSSProperties = {
  padding: "7px 16px", borderRadius: "var(--radius)",
  background: "#ef4444", color: "#fff",
  border: "none", fontSize: 13,
  cursor: "pointer", fontFamily: "var(--font-sans)", fontWeight: 500,
};

// ── Main page ──────────────────────────────────────────────────────────────────

export default function MMMPage() {
  const navigate = useNavigate();
  const [runs, setRuns]             = useState<MMMRun[]>([]);
  const [filter, setFilter]         = useState<FilterKey>("completed");
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [apiError, setApiError]     = useState<string | null>(null);
  const [toDelete, setToDelete]     = useState<MMMRun | null>(null);
  const [deleting, setDeleting]     = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    setApiError(null);
    try {
      const [cfgRes, runRes] = await Promise.allSettled([
        axiosInstance.get("/api/jobConfig/jobType/MMM?limit=200"),
        axiosInstance.get("/api/jobRun/jobType/MMM?limit=200"),
      ]);

      if (cfgRes.status === "rejected") {
        const msg = (cfgRes.reason as any)?.response?.data?.detail || (cfgRes.reason as any)?.message || "Failed to load job configs";
        setApiError(`Job configs API error: ${msg}`);
        return;
      }
      if (runRes.status === "rejected") {
        const msg = (runRes.reason as any)?.response?.data?.detail || (runRes.reason as any)?.message || "Failed to load job runs";
        setApiError(`Job runs API error: ${msg}`);
      }

      const configs: any[] = Array.isArray(cfgRes.value.data)
        ? cfgRes.value.data
        : cfgRes.value.data?.items ?? [];
      const runRows: any[] = runRes.status === "fulfilled"
        ? (Array.isArray(runRes.value.data) ? runRes.value.data : runRes.value.data?.items ?? [])
        : [];

      // Build map: jobConfigId → latest run
      const latestRun: Record<string, any> = {};
      for (const r of runRows) {
        const cid = r.jobConfigId;
        if (!cid) continue;
        if (!latestRun[cid] || new Date(r.createdDate || r.jobExecutionTime || 0) > new Date(latestRun[cid].createdDate || latestRun[cid].jobExecutionTime || 0)) {
          latestRun[cid] = r;
        }
      }

      const mapped: MMMRun[] = configs.map((jc: any) => {
        const id     = jc.jobConfigId || jc.id;
        const run    = latestRun[id];
        const params = jc.modelParameterConfig?.parameters || {};
        const mapping = jc.dataMappingConfig?.mappings?.mmm?.columnMapping || {};

        return {
          id,
          name:       jc.name || "MMM Run",
          status:     mapStatus(jc.status || "", run?.jobStatus),
          createdAt:  jc.createdDate || jc.createdAt || new Date().toISOString(),
          channels:   mapping.media_variables || [],
          modelType:  params.modelType || undefined,
          isSeasonal: params.isSeasonal === true || params.isSeasonal === "true",
          jobRunId:   run?.jobRunId,
        };
      });

      mapped.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setRuns(mapped);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || "Unknown error";
      setApiError(`Failed to load MMM runs: ${msg}`);
      console.error("Failed to load MMM runs", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while any run is active
  useEffect(() => {
    const hasActive = runs.some(r => r.status === "running" || r.status === "pending");
    if (hasActive) {
      timerRef.current = setInterval(() => load(true), 15_000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [runs, load]);

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/api/jobConfig/${toDelete.id}`);
      setRuns(prev => prev.filter(r => r.id !== toDelete.id));
      setToDelete(null);
    } catch (err) {
      console.error("Delete failed", err);
    } finally {
      setDeleting(false);
    }
  };

  const goManualSetup = () => { const id = uuidv4(); navigate(`/mmm/${id}`); };
  const goAgent = () => navigate("/assistant");
  // Query params (not router state) so this is identical to, and shares code
  // with, the link a job-completed notification deep-links to — refresh-safe
  // and bookmarkable. Starts at step 0 (Step 1, Upload) like the rest of the
  // wizard; isViewOnly just disables validation/resubmission as you click Next.
  const goView = (run: MMMRun) => navigate(`/mmm/${run.id}?jobConfigId=${run.id}&viewOnly=true`);

  const displayed = runs.filter(r => matchFilter(r, filter));
  const counts = {
    active:    runs.filter(r => matchFilter(r, "active")).length,
    completed: runs.filter(r => r.status === "completed").length,
    failed:    runs.filter(r => r.status === "failed").length,
  };

  return (
    <div style={{ padding: "28px 32px", maxWidth: 1100, margin: "0 auto", fontFamily: "var(--font-sans)" }}>

      <style>{`
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.7); }
        }
        @keyframes indeterminate {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 style={{ fontSize: 22, fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>MMM</h1>
            {refreshing && (
              <span style={{ fontSize: 11, color: "var(--text-tertiary)", fontFamily: "var(--font-sans)" }}>Refreshing…</span>
            )}
          </div>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "4px 0 0" }}>
            Marketing Mix Models
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => load(true)}
            title="Refresh"
            style={{
              padding: "9px 12px", borderRadius: "var(--radius)",
              background: "var(--bg-surface)", color: "var(--text-secondary)",
              border: "1px solid var(--border-default)", fontSize: 14,
              cursor: "pointer", lineHeight: 1,
            }}
          >
            ↻
          </button>
          <button
            onClick={goManualSetup}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "9px 16px", borderRadius: "var(--radius)",
              background: "var(--bg-surface)", color: "var(--text-secondary)",
              fontSize: 13, fontWeight: 500, cursor: "pointer",
              border: "1px solid var(--border-default)", fontFamily: "var(--font-sans)",
              transition: "opacity 150ms ease",
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "0.75")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
          >
            <span style={{ fontSize: 15, lineHeight: 1 }}>⚙</span>
              New MMM
          </button>
          <button
            onClick={goAgent}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "9px 16px", borderRadius: "var(--radius)",
              background: "var(--blue)", color: "#fff",
              fontSize: 13, fontWeight: 500, cursor: "pointer",
              border: "none", fontFamily: "var(--font-sans)",
              transition: "opacity 150ms ease",
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "0.85")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
          >
            <span style={{ fontSize: 15, lineHeight: 1 }}>✦</span>
              AI Mode
          </button>
        </div>
      </div>

      {/* ── API error banner ── */}
      {apiError && (
        <div style={{
          marginBottom: 16, padding: "10px 14px", borderRadius: "var(--radius)",
          background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
          color: "#ef4444", fontSize: 12, fontFamily: "var(--font-sans)",
          display: "flex", alignItems: "flex-start", gap: 8,
        }}>
          <span style={{ flexShrink: 0, fontWeight: 600 }}>Error</span>
          <span style={{ wordBreak: "break-word" }}>{apiError}</span>
          <button
            onClick={() => setApiError(null)}
            style={{ marginLeft: "auto", background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 14, flexShrink: 0 }}
          >✕</button>
        </div>
      )}

      {/* ── Filter tabs ── */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {FILTERS.map(({ key, label }) => {
          const active = filter === key;
          const count  = counts[key];
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: "5px 12px", borderRadius: 20, fontSize: 12,
                fontFamily: "var(--font-sans)", fontWeight: active ? 500 : 400,
                background: active ? "var(--blue-subtle)" : "var(--bg-surface)",
                color:      active ? "var(--blue)"        : "var(--text-secondary)",
                border:     `1px solid ${active ? "var(--blue)" : "var(--border-default)"}`,
                cursor: "pointer", transition: "all 150ms ease",
              }}
            >
              {label}{" "}
              <span style={{ fontFamily: "var(--font-mono)", color: active ? "var(--blue)" : "var(--text-tertiary)" }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Grid ── */}
      {loading ? (
        <SkeletonGrid />
      ) : displayed.length === 0 ? (
        <EmptyState filter={filter} hasAny={runs.length > 0} onCreate={goAgent} onManual={goManualSetup} />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {displayed.map(run => (
            <RunCard
              key={run.id}
              run={run}
              onClick={() => goView(run)}
              onDelete={e => { e.stopPropagation(); setToDelete(run); }}
            />
          ))}
        </div>
      )}

      {/* ── Delete confirm ── */}
      {toDelete && (
        <DeleteModal
          run={toDelete}
          onConfirm={handleDelete}
          onCancel={() => setToDelete(null)}
          loading={deleting}
        />
      )}
    </div>
  );
}

// ── Skeleton loader ────────────────────────────────────────────────────────────

const SkeletonGrid = () => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
    {[1, 2, 3, 4].map(i => (
      <div key={i} style={{
        height: 150, borderRadius: "var(--radius)",
        background: "linear-gradient(90deg, var(--bg-surface) 25%, var(--bg-hover) 50%, var(--bg-surface) 75%)",
        backgroundSize: "800px 100%",
        animation: "shimmer 1.5s ease-in-out infinite",
        border: "1px solid var(--border-subtle)",
      }} />
    ))}
  </div>
);

// ── Empty state ────────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ filter: FilterKey; hasAny: boolean; onCreate: () => void; onManual: () => void }> = ({ filter, hasAny, onCreate, onManual }) => (
  <div style={{ textAlign: "center", padding: "80px 24px" }}>
    <div style={{ fontSize: 44, marginBottom: 16, opacity: 0.6 }}>📈</div>
    <p style={{ fontSize: 15, fontWeight: 500, color: "var(--text-secondary)", marginBottom: 6, fontFamily: "var(--font-sans)" }}>
      {hasAny ? `No ${filter} MMM runs` : "No MMM runs yet"}
    </p>
    <p style={{ fontSize: 13, color: "var(--text-tertiary)", marginBottom: 24, fontFamily: "var(--font-sans)" }}>
      {hasAny ? "Change the filter or create a new run" : "Run your first Marketing Mix Model"}
    </p>
    {!hasAny && (
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button
          onClick={onManual}
          style={{
            padding: "9px 20px", borderRadius: "var(--radius)",
            background: "var(--bg-surface)", color: "var(--text-secondary)",
            fontSize: 13, cursor: "pointer", border: "1px solid var(--border-default)",
            fontFamily: "var(--font-sans)", fontWeight: 500,
          }}
        >
          ⚙ New MMM
        </button>
        <button
          onClick={onCreate}
          style={{
            padding: "9px 20px", borderRadius: "var(--radius)",
            background: "var(--blue)", color: "#fff",
            fontSize: 13, cursor: "pointer", border: "none",
            fontFamily: "var(--font-sans)", fontWeight: 500,
          }}
        >
          ✦ AI Mode
        </button>
      </div>
    )}
  </div>
);
